import { useCallback, useEffect, useRef, useState } from 'react';
import {
  preSessionReflectionApi,
  type ReflectionStatus,
} from '../../../services/api/preSessionReflection';

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave hook for the pre-session reflection form (MED-803 AC8).
 *
 * Each call to `scheduleSave(questionId, responseText)` resets a per-question
 * debounce timer; when it fires, the response is PUT to the API and the new
 * computed `status` is exposed via the `status` callback.
 *
 * Per-question debounce timers (rather than a single global one) ensure that
 * typing in question A doesn't keep delaying the autosave of question B.
 */
export function useReflectionAutosave(
  sessionId: string | undefined,
  onStatusChange: (status: ReflectionStatus) => void,
  debounceMs: number = 800,
) {
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [state, setState] = useState<AutosaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Clean up any pending timers when the hook unmounts so we don't fire a
  // save after the participant navigates away.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const flushNow = useCallback(
    async (questionId: string, responseText: string) => {
      if (!sessionId) return;
      try {
        setState('saving');
        const result = await preSessionReflectionApi.upsertResponse(
          sessionId,
          questionId,
          responseText,
        );
        onStatusChange(result.status);
        setLastSavedAt(new Date());
        setState('saved');
      } catch {
        setState('error');
      }
    },
    [sessionId, onStatusChange],
  );

  const scheduleSave = useCallback(
    (questionId: string, responseText: string) => {
      if (!sessionId) return;
      const existing = timersRef.current.get(questionId);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        timersRef.current.delete(questionId);
        void flushNow(questionId, responseText);
      }, debounceMs);
      timersRef.current.set(questionId, timer);
    },
    [sessionId, debounceMs, flushNow],
  );

  /** Used by the explicit Save button — flushes ALL pending debounced timers. */
  const saveAllPending = useCallback(
    async (
      pending: Array<{ question_id: string; response_text: string }>,
    ): Promise<ReflectionStatus | null> => {
      if (!sessionId) return null;
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current.clear();
      try {
        setState('saving');
        const result = await preSessionReflectionApi.bulkSave(sessionId, pending);
        onStatusChange(result.status);
        setLastSavedAt(new Date());
        setState('saved');
        return result.status;
      } catch {
        setState('error');
        return null;
      }
    },
    [sessionId, onStatusChange],
  );

  return { state, lastSavedAt, scheduleSave, saveAllPending };
}
